/**
 * FilterSection Component
 * 본사 입출금현황 검색 필터 섹션
 */
import React, { useMemo } from 'react';
import DateInput from '@/shared/components/forms/DateInput';
import SearchableSelect, { SelectOption } from '@/shared/components/forms/SearchableSelect';
import { WithdrawHistoryFilters } from './withdrawHistory.helpers';
import { SupplierOption, RiderOption } from '../hooks/useHeadWithdrawData';
import { getItem } from '@/shared/utils/storage';
import '@/shared/styles/search-form.css';

interface FilterSectionProps {
    /** 필터 상태 */
    filters: WithdrawHistoryFilters;
    /** 본사 사용자 여부 */
    isHeadUser: boolean;
    /** 협력사 사용자 여부 */
    isSupplierUser?: boolean;
    /** 협력사 옵션 목록 */
    supplierOptions: SupplierOption[];
    /** 라이더 옵션 목록 */
    riderOptions: RiderOption[];
    /** 필터 변경 핸들러 */
    onFilterChange: (filters: WithdrawHistoryFilters) => void;
    /** 검색 버튼 클릭 핸들러 */
    onSearch: () => void;
    /** 출금신청 버튼 클릭 핸들러 */
    onWithdrawClick: () => void;
    /** 협력사 선택 시 라이더 목록 조회 핸들러 */
    onSupplierChange: (fromDate: string, toDate: string, supplierName: string) => void;
    /** 엑셀 다운로드 핸들러 */
    onExcelDownload: () => void;
}

const FilterSection: React.FC<FilterSectionProps> = ({
                                                         filters,
                                                         isHeadUser,
                                                         isSupplierUser = false,
                                                         supplierOptions,
                                                         riderOptions,
                                                         onFilterChange,
                                                         onSearch,
                                                         onWithdrawClick,
                                                         onSupplierChange,
                                                         onExcelDownload
                                                     }) => {
    /** 시작일 변경 핸들러 */
    const handleStartDateChange = (value: string) => {
        onFilterChange({ ...filters, startDate: value });
    };

    /** 종료일 변경 핸들러 */
    const handleEndDateChange = (value: string) => {
        onFilterChange({ ...filters, endDate: value });
    };

    // 로컬스토리지에서 ADMIN_YN 값 확인
    const adminYn = getItem<string | boolean>('ADMIN_YN');
    const isAdminYn = adminYn === true || adminYn === 'true' || adminYn === 'Y';

    // 협력사 옵션을 SelectOption 형식으로 변환
    const supplierSelectOptions: SelectOption[] = useMemo(() => {
        return supplierOptions.map((supplier, index) => ({
            value: supplier.id || `supplier-${index}`,
            label: supplier.name
        }));
    }, [supplierOptions]);

    // 라이더 옵션을 SelectOption 형식으로 변환
    const riderSelectOptions: SelectOption[] = useMemo(() => {
        return riderOptions.map((rider, index) => ({
            value: rider.id || `rider-${index}`,
            label: rider.name
        }));
    }, [riderOptions]);

    return (
        <div className="card search-card">
            <div className="card-body">
                <div className="search-form head-withdraw-search-form">
                    {/* 조회일자 */}
                    <div className="form-group date-range-group">
                        <div className="form-field">
                            <label className="form-label">
                                <i className="fas fa-calendar-alt" />
                                조회일자
                            </label>
                            <div className="date-range-inputs">
                                <DateInput
                                    id="headWithdrawStartDate"
                                    value={filters.startDate}
                                    onChange={handleStartDateChange}
                                />
                                <span className="date-separator">~</span>
                                <DateInput
                                    id="headWithdrawEndDate"
                                    value={filters.endDate}
                                    onChange={handleEndDateChange}
                                />
                            </div>
                        </div>
                    </div>

                    {/* 협력사 - 협력사 사용자일 때는 숨김 */}
                    {!isSupplierUser && (
                        <div className="form-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-building" />
                                    협력사
                                </label>
                                <SearchableSelect
                                    options={supplierSelectOptions}
                                    value={filters.supplierId || ''}
                                    onChange={(value) => {
                                        const selectedSupplier = supplierOptions.find(s => s.id === value);
                                        onFilterChange({
                                            ...filters,
                                            supplierId: value || undefined,
                                            supplierName: selectedSupplier?.name || undefined,
                                            riderId: undefined,
                                            riderName: undefined
                                        });
                                        const supplierName = selectedSupplier?.name || value || '';
                                        onSupplierChange(filters.startDate, filters.endDate, supplierName);
                                    }}
                                    placeholder="협력사 검색..."
                                    showAllOption={true}
                                    allOptionLabel="전체"
                                />
                            </div>
                        </div>
                    )}

                    {/* 라이더 */}
                    <div className="form-group">
                        <div className="form-field">
                            <label className="form-label">
                                <i className="fas fa-motorcycle" />
                                라이더
                            </label>
                            <SearchableSelect
                                options={riderSelectOptions}
                                value={filters.riderId || ''}
                                onChange={(value) => {
                                    const selectedRider = riderOptions.find(r => r.id === value);
                                    onFilterChange({
                                        ...filters,
                                        riderId: value || undefined,
                                        riderName: selectedRider?.name || undefined
                                    });
                                }}
                                placeholder="라이더 검색..."
                                showAllOption={true}
                                allOptionLabel="전체"
                            />
                        </div>
                    </div>

                    {/* 버튼 그룹 */}
                    <div className="form-group button-group">
                        {isHeadUser && isAdminYn && (
                            <button className="btn btn-secondary" onClick={onWithdrawClick}>
                                <i className="fas fa-money-bill-wave" />
                                출금신청
                            </button>
                        )}
                        <button className="btn btn-success" onClick={onExcelDownload}>
                            <i className="fas fa-file-excel" />
                            엑셀
                        </button>
                        <button className="btn btn-primary" onClick={onSearch}>
                            <i className="fas fa-search" />
                            검색
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FilterSection;
